// ProofImageViewer — Phase 18 payment proof viewer button.
//
// ════════════════════════════════════════════════════════════════════
// WHAT IT DOES
// ════════════════════════════════════════════════════════════════════
//
// Renders a button that, when clicked, hits the gated proof endpoint
// for a specific request id, then opens the returned image URL in a
// modal. The gated endpoint enforces:
//
//   * 72-hour expiry (returns 410 if proof_uploaded_at + 72h < NOW())
//   * Atomic 2-view limit (recipient side only, on payout proofs;
//     returns 410 expired_view_limit on the 3rd+ open attempt)
//   * Authorization (caller must be sender / approver / cascade
//     upline — handled server-side)
//
// The viewer doesn't trust client-side state for any of the above;
// every open is a fresh API call, so a user can't bypass the limit
// by leaving the modal open or hitting "back". The button label
// reflects the precomputed sender_proof_state / payout_proof_state
// from the list response — but the click handler always re-fetches.
//
// ════════════════════════════════════════════════════════════════════
// PROPS
// ════════════════════════════════════════════════════════════════════
//
//   endpoint    string         API path that returns
//                              { proof_image_url | payout_proof_url,
//                                views_remaining? } or 410 with
//                              { code: 'expired' | 'expired_view_limit' }
//   state       'none'|'available'|'expired'  Precomputed by the
//                              list endpoint — drives initial button label.
//   label       string         Button label. Default per state.
//   onViewed    () => void     Called after a successful view so the
//                              parent can refetch the row (updates
//                              views_remaining badge).
//   apiGet      (path) => Promise<any>  Injected API client (admin vs
//                              player vs agent — different base URLs).

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../lib/i18n';

export type ProofState = 'none' | 'available' | 'expired';

type Props = {
  endpoint: string;
  state: ProofState;
  label?: string;
  onViewed?: () => void;
  apiGet: (path: string) => Promise<unknown>;
};

// Copy guidelines:
//   • Use "screenshot" not "proof" / "image" — the most non-technical
//     user immediately recognises what's behind the button.
//   • Be explicit about WHAT expired (the screenshot, not the
//     withdrawal). Bare "Expired" was confusing.
// 2026-05-21 — Strings flow through useT() so the viewer flips language
// with the rest of the UI. The keys live under proofViewer.* in the
// shared translations catalogue.

export default function ProofImageViewer({
  endpoint, state, label, onViewed, apiGet,
}: Props) {
  const t = useT();
  const STATE_LABELS: Record<ProofState, string> = {
    none:      t('proofViewer.state.none'),
    available: t('proofViewer.state.available'),
    expired:   t('proofViewer.state.expired'),
  };
  const [openUrl, setOpenUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Transient error (e.g. network failure). Long expiry/limit messages
  // are NOT stuffed in here — those flip the button label instead, so
  // they never expand the row column and break the surrounding layout.
  const [err, setErr] = useState<string | null>(null);
  const [viewsRemaining, setViewsRemaining] = useState<number | null>(null);
  // Local override that flips the button into its "expired" look as
  // soon as the server returns 410. Without this the button would
  // still say "View screenshot" because the parent's `state` prop is
  // computed from the LIST response (which doesn't carry view_count)
  // and only refreshes on the next page load.
  type LocalExpiry = 'view_limit' | 'window_expired' | null;
  const [localExpiry, setLocalExpiry] = useState<LocalExpiry>(null);

  // Effective UI state: server-reported expiry OR a 410 we just got.
  const effectiveState: ProofState =
    localExpiry || state === 'expired' ? 'expired' : state;
  const disabled = effectiveState !== 'available' || busy;
  // Button label — when the limit is the reason, name it precisely so
  // the player understands they hit the 2-view cap (not that the
  // withdrawal itself expired).
  const text = label
    ?? (localExpiry === 'view_limit'
          ? t('proofViewer.state.viewLimit')
          : STATE_LABELS[effectiveState]);

  async function open() {
    if (disabled) return;
    setBusy(true); setErr(null);
    try {
      const r = (await apiGet(endpoint)) as {
        proof_image_url?: string;
        payout_proof_url?: string;
        views_remaining?: number | null;
      };
      const url = r.proof_image_url || r.payout_proof_url;
      if (!url) throw new Error('Server returned no image URL');
      if (typeof r.views_remaining === 'number') setViewsRemaining(r.views_remaining);
      setOpenUrl(url);
      onViewed?.();
    } catch (e: any) {
      const code = e?.code || e?.data?.error?.code;
      // Two distinct expiry reasons — flip the button label, no
      // separate error text, so the row never breaks its layout.
      if (code === 'expired_view_limit') {
        setLocalExpiry('view_limit');
      } else if (code === 'expired') {
        setLocalExpiry('window_expired');
      } else {
        setErr(e?.message || t('proofViewer.error.load'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" onClick={open} disabled={disabled}
              title={localExpiry === 'view_limit'
                ? t('proofViewer.title.viewLimit')
                : undefined}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold transition max-w-full whitespace-normal break-words text-center leading-tight
                ${effectiveState === 'available' && !busy
                  ? 'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/60 hover:bg-violet-100 dark:bg-violet-500/15 dark:text-violet-200 dark:ring-violet-400/30'
                  : 'bg-arena-100/70 text-arena-500 ring-1 ring-inset ring-arena-200/50 cursor-not-allowed dark:bg-arena-700/40 dark:text-neon-200/55 dark:ring-arena-600/40'}`}>
        {/* Camera glyph — universally readable "this is a photo"
            signal. Inline SVG so the viewer ships without coupling to
            any specific icon system (each app embeds its own). */}
        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"
             className="shrink-0"
             fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 7h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
        {busy ? t('proofViewer.loading') : text}
      </button>
      {/* Residual error slot — only for non-expiry errors (network
          failures, malformed responses). Constrained to the column's
          width and wrapping so it never blows the row's layout. */}
      {err && (
        <span className="block max-w-[160px] text-[10.5px] text-rose-700 dark:text-rose-300 whitespace-normal break-words leading-tight">
          {err}
        </span>
      )}
      {openUrl && typeof document !== 'undefined' && createPortal(
        // ─────────────────────────────────────────────────────────────
        // RENDERED VIA REACT PORTAL → document.body
        // ─────────────────────────────────────────────────────────────
        // A z-index alone is NOT enough. z-index is scoped to the
        // nearest STACKING CONTEXT — and any ancestor with
        // `transform`, `filter`, `opacity < 1`, `backdrop-filter`,
        // `will-change`, or `isolation: isolate` creates a new one.
        // The player Wallet page wraps its cards in elements that do
        // exactly that, so a `position: fixed; z-[9999]` lightbox
        // rendered INSIDE the card tree was still trapped behind the
        // "Your history by game" / "Recent activity" cards.
        //
        // Mounting via createPortal(..., document.body) detaches the
        // overlay from the component tree and pins it to <body>, the
        // root stacking context — escaping every ancestor trap.
        // 2026-05-21 — Capped at 75% of viewport on both axes
        // (was max-w-3xl + max-h-[90vh]). Reason: tall portrait
        // screenshots were stretching to near-100% viewport height,
        // which pushed the close button (positioned `-top-3` outside
        // the image's top-right corner) up behind the phone's notch
        // / status bar on iPhones with Dynamic Island and most
        // Android devices with a punch-hole camera. The hard 75dvh
        // cap guarantees at least 12.5% clearance on top + bottom,
        // and using `dvh` (dynamic viewport height) instead of `vh`
        // also accounts for the iOS Safari address-bar collapse so
        // the math holds during scroll.
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/75 backdrop-blur-sm p-4"
             onClick={() => setOpenUrl(null)}>
          <div className="relative max-w-[75vw] max-h-[75dvh]" onClick={(e) => e.stopPropagation()}>
            <img src={openUrl} alt={t('proofViewer.imageAlt')}
                 className="max-w-[75vw] max-h-[75dvh] rounded-xl shadow-2xl bg-white" />
            <button onClick={() => setOpenUrl(null)} aria-label={t('proofViewer.close')}
                    className="absolute -top-3 -right-3 grid place-items-center w-9 h-9 rounded-full text-white
                               bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_4px_14px_rgba(244,63,94,0.55)]">
              ×
            </button>
            {viewsRemaining != null && (
              <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold
                              bg-black/65 text-white">
                {viewsRemaining === 0
                  ? t('proofViewer.lastView')
                  : t(
                      viewsRemaining === 1
                        ? 'proofViewer.viewsRemaining.one'
                        : 'proofViewer.viewsRemaining.other',
                      { n: viewsRemaining },
                    )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
